import type { FunctionViewModel, MeetingOccurrenceViewModel, ReportViewModel } from '@trinnvis/dabih-api-client';
import { BASE_PATH } from '@trinnvis/dabih-api-client';
import type { ContactsAsUsers, MeetingModel, NameEntry } from 'src/store/selectors';
import {
  currentEmployeeShortName,
  currentEmployeeUuid,
  currentUserUuid,
  employeesDeleted,
  employeesNotDeleted,
  employeesShortNamesAndI,
  functionsNotDeleted,
  getClassificationText,
  getContactsAsUsers,
  getOrganization,
  isRestricted,
  key,
  meetings,
  meetingsNotDeleted,
  partnersNotDeleted,
  reportsNotDeleted,
  singleFunctionsWithEmployeeNames,
  writeAccess,
} from 'src/store/selectors';
import type { OrganizationState, State } from 'src/store/types';
import { LocalDate } from 'src/utilities/local-date';
import { reminderText, dateTimeDescription, toDateTime } from 'src/store/utilities';
import { findLast, sortBy } from 'lodash';
import { capitalize, displayName, isEmptyOrInvalidString, minutesToTime } from 'src/utilities/text';
import type { ApiClient } from 'src/store/backend';
import { getRelatedCollections } from 'src/store/selectors/collections';
import { LocalDateTime } from 'src/utilities/local-date-time';
import { getPromoDataForEntity } from 'src/store/selectors/promo';
import type { Occurrence } from '../../library/lists/occurrence';
import type { CommonDataEntityView } from '../../content/common-data-entity-view';
import type { MeetingOccurrenceViewEditItem } from '../../content/meeting-occurrences/meeting-occurrence-view-edit-item';
import type { ModelMeeting } from '../../content/meeting-occurrences/model-meeting';
import type { MeetingOccurrenceView } from '../../content/meeting-occurrences/meeting-occurrence-view';
import { assertIsDefined } from '../../assertIsDefined';
import { assertEntityFound } from './entity-not-found';
import { toAttachmentItems } from '../to-attachment-items';
import { fetchDraft } from '../fetch-draft';
import { getFeatureStates } from '../../store/selectors/features/getFeatureStates';
import { meetingParticipantsText } from '../../store/meetingParticipantsText';
import { functionNameWithAssignedNames } from '../../store/functionNameWithAssignedNames';
import { getUsersForAccess } from '../get-users-for-access';
import { availableMeetingMessageRecipients, meetingParticipants } from 'src/store/selectors/meetings/meetings';
import { t } from 'src/i18n';

function noticeSentText(sent: string | undefined) {
  if (sent) {
    return t('meetings.noticeSentAt', {
      date: LocalDate.fromString(sent.substring(0, 10)).toStringForDisplayWithDayOfWeekAndYear(),
      time: sent.substring(11, 16).replace(':', '.'),
    });
  }
  return t('meetings.noticeNotSent');
}
function reportSharedText(shared: string | undefined) {
  if (shared) {
    return t('meetings.reportSharedAt', {
      date: LocalDate.fromString(shared.substring(0, 10)).toStringForDisplayWithDayOfWeekAndYear(),
      time: shared.substring(11, 16).replace(':', '.'),
    });
  }

  return t('meetings.reportNotShared');
}
function getFutureOccurrences(
  occurrences: MeetingOccurrenceViewModel[],
  functions: FunctionViewModel[],
  employeesShortNamesAndI: NameEntry[],
  contactsAsUsers: ContactsAsUsers[],
  currentUserUuidValue: string,
): Occurrence[] {
  const today = LocalDate.now();
  return occurrences
    .filter((o) => {
      return LocalDate.fromString(o.date).isAfter(today);
    })
    .map((o) => {
      return {
        occurrenceUuid: o.uuid,
        href: '',
        classified: o.classification && o.classification !== 'NONE',
        restricted: isRestricted(o, currentUserUuidValue),
        disabled: false,
        dateTime: o.date + ' ' + o.time,
        durationMinutes: o.durationMinutes,
        displayDate: dateTimeDescription(o.date ?? '', o.time ?? '', o.durationMinutes),
        assignees: meetingParticipantsText(o, functions, employeesShortNamesAndI, contactsAsUsers, true),
        meetingStatus: 'NONE',
      };
    });
}

function listModelMeetings(
  meetingModels: MeetingModel[],
  meetings,
  organization: OrganizationState,
  functions: FunctionViewModel[],
  employeesShortNamesAndI: NameEntry[],
  contactsAsUsers: ContactsAsUsers[],
  currentUserUuidValue: string,
): ModelMeeting[] {
  return meetingModels.map((m) => {
    const accessibleOccurrences = m.meetingOccurrences.filter((e) => !e.restricted);
    let name = displayName(m.name);
    let isEmployeeInterview = false;
    if (m.type === 'INTERVIEW_ACTIVE' || m.type === 'INTERVIEW_TERMINATED') {
      name = t('meetings.employeeInterviewWithName', { name: m.interviewEmployeeName });
      isEmployeeInterview = true;
    }
    let meetingResponsible = '';
    let agenda = '';
    const meetingTemplate = meetings.find((t) => {
      return t.name === m.name;
    });
    if (meetingTemplate) {
      meetingResponsible = meetingTemplate.functionUuid || '';
      agenda = meetingTemplate.agenda;
    }
    if (accessibleOccurrences.length === 0) {
      return {
        name,
        isEmployeeInterview,
        meetingResponsible,
        meetingResponsibleEmployee: '',
        employees: isEmployeeInterview ? [m.interviewEmployeeUuid] : [],
        contacts: [],
        notes: '',
        agenda,
        meetingUuid: m.uuid,
        classification: 'NONE',
        accessControl: [],
        time: '12:00',
        durationMinutes: 0,
        futureOccurrences: [],
      };
    } else {
      const today = LocalDate.now().toString();
      const sortedOccurrences = sortBy(accessibleOccurrences, [(e) => e.date]);
      const modelOccurrence = findLast(sortedOccurrences, (e) => e.date <= today) ?? sortedOccurrences[0];
      const x = organization.meetingOccurrences.find((e) => e.uuid === modelOccurrence.uuid);
      assertIsDefined(x);
      const occurrences = organization.meetingOccurrences.filter((e) => {
        if (e.name === 'Medarbeidersamtale') {
          return e.name === x.name && e.participants === x.participants;
        }
        return e.name === x.name;
      });
      const futureOccurrences = getFutureOccurrences(
        occurrences,
        functions,
        employeesShortNamesAndI,
        contactsAsUsers,
        currentUserUuidValue,
      );
      return {
        name,
        isEmployeeInterview,
        meetingResponsible: x.responsibleUuid || '',
        meetingResponsibleEmployee: x.responsibleEmployeeUuid || '',
        employees: x.participants,
        contacts: x.externalParticipants,
        notes: '',
        agenda: x.agenda,
        meetingUuid: m.uuid || x.meetingUuid,
        classification: x.classification,
        accessControl: x.accessControl,
        time: x.time || 'NONE',
        durationMinutes: x.durationMinutes,
        futureOccurrences: sortBy(futureOccurrences, ['dateTime']),
      };
    }
  });
}

function timeInMinutes(time: string): number {
  return Number(time.split(':')[0]) * 60 + Number(time.split(':')[1]);
}

function endTime(time: string, durationMinutes: number): string {
  return minutesToTime(timeInMinutes(time) + durationMinutes);
}

function isPast(date: string, time: string, durationMinutes: number): boolean {
  const endDateTime = date + ' ' + endTime(time, durationMinutes);
  const now = LocalDateTime.now();
  const nowDateTime = now.toString();
  return endDateTime < nowDateTime;
}

export async function buildMeetingOccurrenceView(
  api: ApiClient,
  uuid: string,
  commonData: CommonDataEntityView,
  state: State,
  currentParent: string,
): Promise<MeetingOccurrenceView> {
  const organization = getOrganization(state);
  assertIsDefined(organization);

  const currentEmployeeUuid1 = currentEmployeeUuid(state);

  const meetingOccurrence = organization.meetingOccurrences.find((o) => o.uuid === uuid);

  assertEntityFound(meetingOccurrence, 'meetingOccurrences', uuid);

  const meetingModels = meetings(state);

  const item = meetingModels.find((x) => x.meetingOccurrences.map((o) => o.uuid).includes(uuid));

  assertEntityFound(item, 'meetingOccurrences', uuid);

  const entity = meetingOccurrence;

  const relatedMeeting = meetingsNotDeleted(state).find((m) => {
    return m.name === entity.name;
  });

  const singleFunctionsWithEmployeeNames1 = singleFunctionsWithEmployeeNames(state).map((p) => ({
    value: p.uuid,
    text: p.name,
  }));
  const meetingResponsibleFunction = organization.functionsById[entity.responsibleUuid ?? ''];
  const meetingResponsibleFunctionEmployeeUuid = meetingResponsibleFunction?.employees[0];

  const relatedReport: ReportViewModel | undefined = reportsNotDeleted(state).find(
    (report) => report.meetingOccurrenceUuid === uuid,
  );

  let meetingReportIsClassified = false;
  if (relatedReport && relatedReport.classification && relatedReport.classification !== 'NONE') {
    meetingReportIsClassified = true;
  }

  const localDate = LocalDate.fromString(entity.date);
  const canUpdateFutureOccurrences =
    entity.isConfirmedEntity &&
    item.meetingOccurrences.filter((e) => LocalDate.fromString(e.date).isAfter(localDate)).length > 0;

  const allEmployees = employeesDeleted(state).concat(employeesNotDeleted(state));
  const employeeFullNames = allEmployees.map((e) => {
    return { uuid: e.uuid, name: e.name };
  });

  let meetingResponsibleDisplayText: string;
  if (entity.responsibleUuid) {
    meetingResponsibleDisplayText = functionNameWithAssignedNames(
      entity.responsibleUuid,
      functionsNotDeleted(state),
      employeesShortNamesAndI(state),
    );
  } else {
    meetingResponsibleDisplayText =
      employeesShortNamesAndI(state).find((e) => {
        return e.uuid === entity['responsibleEmployeeUuid'];
      })?.name ?? '';
  }

  const availablePartners = partnersNotDeleted(state)
    .filter((p) => {
      return p.templateId === null || !p.contentLastModifiedWasCreate;
    })
    .map((p) => {
      return { value: p.uuid, text: p.name };
    });

  const hasFunctions = getFeatureStates(state).core || getFeatureStates(state).hse;

  const participants = meetingParticipants(
    meetingResponsibleFunctionEmployeeUuid,
    entity.responsibleEmployeeUuid,
    entity.participants ?? [],
    entity.externalParticipants,
  );

  return {
    ...commonData,
    promoData: getPromoDataForEntity('meetingOccurrences', item, getFeatureStates(state)),
    agendaDescriptionLabel: '',
    relatedReport,
    hasReport: relatedReport !== undefined,
    doneDate: entity.doneDate ?? '',
    doneText: entity.doneDate
      ? LocalDate.fromString(entity.doneDate).toStringForDisplay()
      : t('meetings.notRegistered'),
    isPast: isPast(entity.date, entity.time ?? '00:00', entity.durationMinutes),
    meetingDone: entity.doneDate !== undefined && entity.doneDate !== null && entity.doneDate !== '',
    meetingReportWrittenLabel: relatedReport ? t('meetings.reportWritten') : t('meetings.reportNotWritten'),
    meetingReportWrittenUuid: relatedReport?.uuid,
    meetingReportWrittenText: relatedReport
      ? t('meetings.reportWrittenAt', {
          date: LocalDate.fromString(relatedReport.reportDate).toStringForDisplayWithDayOfWeekAndYear(),
        })
      : t('meetings.reportNotWrittenShort'),
    meetingReportIsClassified,
    meetingReportContent: relatedReport?.content,
    meetingResponsibleDisplayText: capitalize(meetingResponsibleDisplayText),
    noticeSent: entity.noticeLastSent !== undefined && entity.noticeLastSent !== null && entity.noticeLastSent !== '',
    noticeSentText: noticeSentText(entity.noticeLastSent),
    reportShared: entity.reportLastSent !== undefined && entity.reportLastSent !== null && entity.reportLastSent !== '',
    reportSharedText: reportSharedText(entity.reportLastSent),
    currentUserHasWriteAccess: writeAccess(state),
    isConfirmedEntity: entity.isConfirmedEntity,
    newItem: !entity.isConfirmedEntity,
    uuid: uuid,
    modelMeetings: listModelMeetings(
      meetingModels.filter((m) => {
        return m.type !== 'INTERVIEW_TERMINATED';
      }),
      meetingsNotDeleted(state),
      organization,
      functionsNotDeleted(state),
      employeesShortNamesAndI(state),
      getContactsAsUsers(state),
      currentUserUuid(state),
    ),
    templateMeetings: meetingsNotDeleted(state).map((m) => {
      return {
        name: m.name || '',
        uuid: m.uuid,
        helpContent: m.helpContent || '',
        agenda: m.procedures || '',
      };
    }),
    helpContent: relatedMeeting?.helpContent ?? '',
    href: currentParent + '/meetingOccurrences/' + uuid,
    pdfHref:
      BASE_PATH +
      '/organizations/' +
      organization.organizationId +
      '/meetingOccurrences/' +
      uuid +
      '.pdf?key=' +
      key(state),
    parentHref: currentParent,
    deleted: false,
    reminderText: reminderText(entity.reminder),
    assigneeDisplayText: capitalize(
      meetingParticipantsText(entity, [], employeesShortNamesAndI(state), getContactsAsUsers(state)),
    ),
    notes: '',
    timeDescription: dateTimeDescription(entity.date, entity.time, entity.durationMinutes),
    type: 'meetingOccurrences',
    name: entity.name ?? '',
    attachments: toAttachmentItems(api, 'meetingOccurrences', entity.uuid, entity.attachments),
    thisEventInstance: '',
    today: LocalDate.fromString(state.today),
    functions: functionsNotDeleted(state),
    availableEmployees: sortBy(
      employeesShortNamesAndI(state)
        .filter((e) => e.name !== '')
        .filter((e) => e.status !== 'TERMINATED' || (entity.participants ?? []).includes(e.uuid))
        .map((p) => ({ value: p.uuid, text: p.name })),
      ['text'],
    ),
    availableContacts: sortBy(
      getContactsAsUsers(state)
        .filter((p) => {
          return !isEmptyOrInvalidString(p.name);
        })
        .map((p) => ({ value: p.uuid, text: p.name.trim() + ' (' + p.partnerName + ')' })),
      ['text'],
    ),
    availableFunctions: hasFunctions ? sortBy(singleFunctionsWithEmployeeNames1, ['text']) : [],
    participants,
    availableMeetingMessageRecipients: availableMeetingMessageRecipients(
      employeesShortNamesAndI(state),
      employeesNotDeleted(state),
      getContactsAsUsers(state),
      participants,
      entity.externalParticipants,
      entity.name?.toLowerCase().includes('medarbeidersamtale') ?? false,
    ),
    currentEmployeeUuid: currentEmployeeUuid1,
    defaultMeetingResponsibleFunctionUuid: sortBy(singleFunctionsWithEmployeeNames1, ['text'])[0]?.value ?? '',
    defaultMeetingResponsibleEmployeeUuid: currentEmployeeUuid1 ?? '',
    currentEmployeeShortName: currentEmployeeShortName(state),
    currentUserUuid: currentUserUuid(state),
    employeeFullNames,
    deletable: true,
    hasDraft: meetingOccurrence.hasDraft,
    fetchDraft: async () => {
      const command = await fetchDraft(api, 'meetingOccurrences', uuid, organization.organizationId);
      return command.draft as MeetingOccurrenceViewEditItem;
    },
    date: entity.date,
    time: entity.time ?? '12:00',
    durationMinutes: entity.durationMinutes,
    assignedToEmployees: entity.participants ?? [],
    reminder: entity.reminder ?? 'NONE',
    accessControl: entity.accessControl,
    classification: entity.classification,
    classificationText: getClassificationText(
      entity.classification ?? 'NONE',
      entity.accessControl ?? [],
      commonData.accessControlOptions,
    ),
    meetingAgenda: entity.agenda ?? '',
    meetingResponsibleFunctionUuid: hasFunctions ? (entity.responsibleUuid ?? '') : '',
    meetingResponsibleEmployeeUuid: entity.responsibleEmployeeUuid ?? '',
    relatedMeetingProcedures: relatedMeeting?.procedures ?? '',
    currentUserHasAccess:
      entity.classification === 'NONE' || (entity.accessControl ?? []).includes(currentUserUuid(state) ?? ''),
    docsForLinking: [],
    lastModified: entity.lastModified ?? '',
    templateUpdated: relatedMeeting?.templateUpdated ?? '',
    occurrences: item.meetingOccurrences
      .filter((o) => {
        return o.isConfirmedEntity;
      })
      .map((o) => {
        return {
          occurrenceUuid: o.uuid,
          dateTime: toDateTime(o.date, o.time ?? ''),
          durationMinutes: o.durationMinutes,
          displayDate: o.displayDate,
          assignees: o.assignees,
          classified: o.classified,
          restricted: o.restricted,
          meetingStatus: o.meetingStatus,
          href: '/meetingOccurrences/' + o.uuid,
        };
      }),
    canUpdateFutureOccurrences,
    organizationId: '' + organization.organizationId,
    userEmails: getUsersForAccess(state)
      .map((e) => e.email ?? '')
      .filter((x) => x !== ''),
    assignedToContacts: entity.externalParticipants,
    availablePartners: sortBy(availablePartners, ['text']),
    relatedCollections: getRelatedCollections(commonData.availableCollections, uuid),
    recognizedText: state.recognizedText,
  };
}
